import { Platform } from "react-native"

import { ContentType } from "@treefort/api-spec"
import { Event as AuthEvent } from "@treefort/lib/authenticator"
import { AudioError } from "@treefort/lib/errors"

import { getClient } from "../../watermelon/db"
import { syncManager } from "../../watermelon/sync"
import authenticator from "../authenticator"
import { LoopMode, PlaybackState } from "../av/types"
import downloadManager, {
  Event as DownloadManagerEvent,
} from "../download-manager"
import { logError } from "../logging"
import {
  handlePlayProgressEvent,
  handlePlaybackRateChanged,
  handlePlaybackStateChanged,
  handleTracksChanged,
} from "../play-progress-tracking"
import settings from "../settings"
import { playProgressTracker } from "./play-progress-tracker"
import { AudioPlayer } from "./player"
import { Event } from "./players/base"

export const audioPlayer = new AudioPlayer()

export * from "./players/base"

const LOOP_MODE_KEY = "audioPlayer.loopMode"

const DEFAULT_PLAYBACK_RATE_KEY = "playback.rate.content"

/**
 * This function sets up a host of event listeners to coordinate behavior
 * related to the audio player.
 */
function addEventListeners() {
  // Handle progress updates (save progress, check timers, etc.). On Android
  // these events are setup in playback-service.ts.
  if (Platform.OS !== "android") {
    audioPlayer.on(Event.Progress, ({ id, duration, position }) =>
      handlePlayProgressEvent({
        duration,
        position,
        playbackRate: audioPlayer.getPlaybackRate(),
        tracks: audioPlayer.getTracks(),
        track: audioPlayer.getTrack(id),
        playMode: "listen",
        playProgressTracker,
        pause: audioPlayer.pause,
      }),
    )

    audioPlayer.on(Event.PlaybackState, (playbackState) =>
      handlePlaybackStateChanged({ playbackState, playProgressTracker }),
    )
  }

  audioPlayer.on(Event.PlaybackRate, (playbackRate) =>
    handlePlaybackRateChanged({ playbackRate, playProgressTracker }),
  )

  audioPlayer.on(Event.Tracks, handleTracksChanged)

  audioPlayer.on(Event.Finished, async () => {
    try {
      await audioPlayer.pause()
      const tracks = audioPlayer.getTracks()
      if (tracks.length > 1) {
        await audioPlayer.skipToTrack(tracks[0].id)
      } else {
        await audioPlayer.skipToPosition(0)
      }
    } catch (e) {
      logError(e)
    }
  })

  audioPlayer.on(Event.LoopMode, setLoopMode)

  audioPlayer.on(Event.PlaybackRate, (playbackRate) => {
    const track = audioPlayer.getTrack()
    if (track) {
      setDefaultPlaybackRate({
        contentId: track.extra.contentId,
        contentType: track.extra.contentType,
        profileId: track.extra.profileId,
        playbackRate,
      })
    }
  })

  // Stop the audio player when the user signs out
  authenticator.on(AuthEvent.ActionComplete, async (action) => {
    if (action === "logout") {
      await audioPlayer.stop().catch(logError)
    }
  })

  // Trigger a sync any time the audio player is paused
  audioPlayer.on(Event.PlaybackState, (state) => {
    if (state === PlaybackState.Paused) {
      syncManager.requestSync({ syncType: "immediate" })
    }
  })

  // Update artwork and media file URLs to file URIs when the files are downloaded
  downloadManager.on(DownloadManagerEvent.DownloadSuccess, async (event) => {
    const tracks = audioPlayer.getTracks()

    await Promise.all([
      ...tracks
        .filter((track) => track.extra.urlDownloadKey === event.key)
        .map((track) =>
          audioPlayer.updateTrackUrl(track.id, event.data.fileUri),
        ),
      ...tracks
        .filter((track) => track.extra.artworkDownloadKey === event.key)
        .map((track) =>
          audioPlayer.updateTrackArtwork(track.id, event.data.fileUri),
        ),
    ]).catch((cause) =>
      logError(
        new AudioError(
          "Failed to update audio player after download completion",
          { cause },
        ),
      ),
    )
  })

  // Remove downloaded tracks from the player when the corresponding files are
  // deleted
  downloadManager.on(
    DownloadManagerEvent.DownloadWillDeleteBatched,
    async (event) => {
      const tracks = audioPlayer.getTracks()
      const trackIds = tracks
        .filter(
          (track) =>
            (track.extra.artworkDownloadKey &&
              event.keys.includes(track.extra.artworkDownloadKey)) ||
            (track.extra.urlDownloadKey &&
              event.keys.includes(track.extra.urlDownloadKey)),
        )
        .map((track) => track.id)
      audioPlayer.removeTracks(trackIds)
    },
  )
}

export async function setLoopMode(loopMode: LoopMode) {
  await getClient().localStorage.set(LOOP_MODE_KEY, loopMode)
}

export async function getLoopMode(): Promise<LoopMode> {
  const result = await getClient().localStorage.get<LoopMode>(LOOP_MODE_KEY)
  return result || "off"
}

/**
 * Set the default playback rate that should be used for particular content
 */
const setDefaultPlaybackRate = ({
  contentId,
  contentType,
  profileId,
  playbackRate,
}: {
  contentId: number
  contentType: ContentType
  profileId: string | null
  playbackRate: number
}) => {
  return contentType === "podcast"
    ? settings.saveLocal(
        `${DEFAULT_PLAYBACK_RATE_KEY}.${contentId}`,
        playbackRate,
        {
          profileId,
        },
      )
    : undefined
}

/**
 * Get the default playback rate that should be used for particular content
 */
export const getDefaultPlaybackRate = ({
  contentId,
  contentType,
  profileId,
}: {
  contentId: number
  contentType: ContentType
  profileId: string | null
}) => {
  return contentType === "podcast"
    ? settings
        .getLocal<number>(`${DEFAULT_PLAYBACK_RATE_KEY}.${contentId}`, {
          profileId,
        })
        .then((setting) => setting.value)
    : null
}

addEventListeners()
